import React, { useMemo, useState } from 'react';
import { Popover } from '@mui/material';
import scogo_apis from '../../../scogo_apis';
import { ColumnLoader } from '../components/SP/SPTable';
import RatingComponent from '../../../common/Rating';
import { useSelector } from 'react-redux';
import { getLookupWitHDefaultValue } from '../../../utils/common';

export const UserStatus = getLookupWitHDefaultValue({
    lookup: {
        ACTIVE: 'ACTIVE',
        INACTIVE: 'INACTIVE',
        PROFILE_PENDING: 'PROFILE_PENDING',
        VERIFICATION_PENDING: 'VERIFICATION_PENDING',
        ADDRESS_PROOF_NOT_UPDATED: 'ADDRESS_PROOF_NOT_UPDATED',
        KYC_PENDING: 'KYC_PENDING'
    },
    defaultValue: 'ACTIVE',
});

export const DisabledDuration = {
    PERMANENT: 'PERMANENT',
    SEVEN_DAYS: 'SEVEN_DAYS',
    FIFTEEN_DAYS: 'FIFTEEN_DAYS',
};

function getStatusDetails({ status, disabled_duration, is_verified, is_onboarded, is_kyc_verified, kyc_list }) {
    if (!is_onboarded) {
        return { color: 'text-scogobgsky', label: 'Onboarding Pending' };
    };

    if (status === UserStatus.INACTIVE) {
        const durationLabels = {
            [DisabledDuration.PERMANENT]: 'Permanently disabled',
            [DisabledDuration.SEVEN_DAYS]: 'Disabled for 7 days',
            [DisabledDuration.FIFTEEN_DAYS]: 'Disabled for 15 days',
        };

        const label = durationLabels[disabled_duration] || 'Inactive';
        return { color: 'text-scogoclosed', label };
    };

    if (!is_verified) {
        return { color: 'text-scogoclosed', label: 'Verification Pending' };
    };
    if (status === UserStatus.ACTIVE) {
        return { color: 'text-scogo2e', label: 'Active' };
    };



    const statusMap = {
        [UserStatus.PROFILE_PENDING]: { color: 'text-scogobgsky', label: 'Profile Pending' },
        [UserStatus.VERIFICATION_PENDING]: { color: 'text-scogoclosed', label: 'Verification Pending' },
        [UserStatus.ADDRESS_PROOF_NOT_UPDATED]: { color: 'text-scogoclosed', label: 'Address Proof Not Updated' },
    };

    return statusMap[status] || { color: 'text-scogoclosed', label: 'Inactive' };
}


// "Auto Accept" badge; clicking it shows the user's selected auto-accept projects (fetched from getUserDetail).
const AutoAcceptBadge = ({ userId }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [projects, setProjects] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const fetchProjects = async () => {
        setIsLoading(true);
        setHasError(false);
        try {
            const response = await scogo_apis.user.getUserDetail({}, { userId }, {});
            const responseUser = response?.data?.data?.user || response?.data?.user || {};
            const list = Array.isArray(responseUser?.auto_accept_projects)
                ? responseUser.auto_accept_projects.filter((project) => project && project.id !== undefined && project.id !== null)
                : [];
            setProjects(list);
        } catch (error) {
            setHasError(true);
        }
        setIsLoading(false);
    };

    const onClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        if (projects === null && !isLoading) fetchProjects();
    };

    const onClose = (event) => {
        event?.stopPropagation?.();
        setAnchorEl(null);
    };

    return <>
        <span
            role='button'
            onClick={onClick}
            title='View auto accept projects'
            className='inline-flex items-center gap-1 mb-1 px-p-6 py-p-3 rounded-full text-font08 text-scogoorange bg-scogoorange bg-opacity-10 border border-scogoorange w-fit cursor-pointer hover:bg-opacity-20'
        >
            <span className='material-icons text-font10'>task_alt</span>
            Auto Accept
        </span>
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            onClick={(event) => event.stopPropagation()}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { mt: 1, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)', border: '1px solid #f1f1f1' } }}
        >
            <div className='w-96 max-w-[90vw]'>
                <div className='flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-orange-50 to-white border-b border-scogoeee'>
                    <span className='flex items-center justify-center w-9 h-9 rounded-full bg-scogoorange bg-opacity-10 shrink-0'>
                        <span className='material-icons text-scogoorange text-font18'>task_alt</span>
                    </span>
                    <div className='flex-1 min-w-0'>
                        <p className='text-font13 font-semibold text-scogo15 leading-tight'>Auto Accept Projects</p>
                    </div>
                    {Array.isArray(projects) && !isLoading && (
                        <span className='inline-flex items-center justify-center min-w-[1.6rem] h-6 px-2 rounded-full text-font10 font-semibold text-white bg-scogoorange shrink-0'>{projects.length}</span>
                    )}
                </div>
                <div className='p-2'>
                    {isLoading ? (
                        <div className='py-4 flex justify-center'><ColumnLoader /></div>
                    ) : hasError ? (
                        <div className='flex flex-col items-center gap-1 py-4 text-center'>
                            <span className='material-icons text-scogoclosed'>error_outline</span>
                            <p className='text-font11 text-scogo15'>Could not load projects</p>
                            <span role='button' onClick={fetchProjects} className='text-font11 font-medium text-scogoorange hover:underline cursor-pointer'>Retry</span>
                        </div>
                    ) : Array.isArray(projects) && projects.length > 0 ? (
                        <ul className='max-h-56 overflow-y-auto'>
                            {projects.map((project, index) => (
                                <li key={project.id} className='flex items-start gap-3 px-2 py-2 rounded-lg hover:bg-scogof8 transition-colors'>
                                    <span className='flex items-center justify-center w-7 h-7 rounded-md bg-blue-50 shrink-0'>
                                        <span className='material-icons text-font14 text-scogoprimary'>folder</span>
                                    </span>
                                    <span className='flex-1 min-w-0 break-words text-font12 font-medium text-scogo15 leading-snug'>{project.name}</span>
                                    <span className='text-font10 text-scogo99 shrink-0'>#{index + 1}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className='flex flex-col items-center gap-1 py-4 text-center'>
                            <span className='material-icons text-scogo99'>folder_off</span>
                            <p className='text-font11 text-scogo99'>No projects selected</p>
                        </div>
                    )}
                </div>
            </div>
        </Popover>
    </>;
};

export default function Status({ user }) {
    const userList = useSelector((state) => state.partners.userList);

    const findUser = useMemo(() => {
        let findUser = userList.find((u) => u.id === user.id) || {};
        return findUser;
    }, [user, userList]);

    const rating = (user?.avg_sign_off_quality_rating + user?.avg_work_quality_rating) / 2;
    const status = getStatusDetails({ status: findUser.status, disabled_duration: findUser.disabled_duration, is_verified: findUser.is_verified, is_onboarded: findUser.is_onboarded, is_kyc_verified: findUser.is_kyc_verified, kyc_list: findUser.kyc_list })

    return <>
        <p className={`${status.color} mb-1`}>{status.label}</p>
        {findUser.auto_accept ? <AutoAcceptBadge userId={user.id} /> : null}
        {rating > 0 ? <RatingComponent rating={rating} /> : <></>}
    </>
};